// src/resources/commentReports/CommentReportShow.js
import * as React from 'react';
import {
    Show,
    SimpleShowLayout,
    TextField,
    DateField,
    FunctionField,
} from 'react-admin';
import {
    Avatar,
    Box,
    Card,
    CardContent,
    Divider,
    Stack,
    Typography,
    Chip,
} from '@mui/material';
import CommentReportActionsField from './CommentReportActionsField';
import StatusField from '../../components/StatusField';
import {
    getCommentAuthor,
    getCommentAuthorAvatar,
    getCommentContent,
    getReporterList,
    getReportEntries,
    getReportCount,
    getRdfMeta,
} from './helpers';

const renderCommentCard = (record) => {
    if (!record) return '—';
    const author = getCommentAuthor(record);
    const avatarUrl = getCommentAuthorAvatar(record);
    const content = getCommentContent(record);
    const rdf = getRdfMeta(record);

    return (
        <Card variant="outlined" sx={{ mb: 2 }}>
            <CardContent>
                <Stack direction="row" spacing={2}>
                    <Avatar src={avatarUrl || undefined} sx={{ width: 48, height: 48 }}>
                        {author?.slice(0, 2)?.toUpperCase()}
                    </Avatar>
                    <Box sx={{ flex: 1 }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                            {author}
                        </Typography>
                        <Typography variant="body1" sx={{ whiteSpace: 'pre-line' }}>
                            {content || '—'}
                        </Typography>
                        {rdf?.slug && (
                            <Typography variant="caption" color="text.secondary">
                                RDF : {rdf.slug}
                            </Typography>
                        )}
                    </Box>
                </Stack>
            </CardContent>
        </Card>
    );
};

const renderReporterTimeline = (record) => {
    if (!record) return '—';
    const reporters = getReporterList(record);
    const entries = getReportEntries(record);

    if (!reporters.length && !entries.length) {
        return <Typography variant="body2">Aucun détail supplémentaire.</Typography>;
    }

    return (
        <Box>
            {reporters.length > 0 && (
                <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mb: 1 }}>
                    {reporters.map((user) => (
                        <Chip
                            key={user?.id || user?.username}
                            label={user?.username || 'Anonyme'}
                            avatar={
                                user?.avatarUrl ? (
                                    <Avatar src={user.avatarUrl} />
                                ) : undefined
                            }
                        />
                    ))}
                </Stack>
            )}
            <Divider sx={{ my: 1 }} />
            <Stack spacing={1.5}>
                {entries.map((entry) => (
                    <Box
                        key={entry.id || `${entry.reason}-${entry.createdAt}`}
                        sx={{ borderLeft: '3px solid #e0e0e0', pl: 2 }}
                    >
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {entry.reporter?.username || entry.reporterUsername || 'Utilisateur'}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            {entry.reason || 'Motif non fourni'}
                        </Typography>
                        {entry.createdAt && (
                            <Typography variant="caption" color="text.secondary">
                                {new Date(entry.createdAt).toLocaleString()}
                            </Typography>
                        )}
                    </Box>
                ))}
                {entries.length === 0 && (
                    <Typography variant="body2" color="text.secondary">
                        Aucun signalement détaillé reçu.
                    </Typography>
                )}
            </Stack>
        </Box>
    );
};

const renderActions = (record) => <CommentReportActionsField variant="show" record={record} />;

export default function CommentReportShow() {
    return (
        <Show resource="comment-reports" title="Signalement de commentaire">
            <SimpleShowLayout>
                <TextField source="id" label="ID" />
                <TextField source="commentId" label="Commentaire ID" />
                <StatusField source="status" label="Statut" />
                <FunctionField
                    label="Total signalements"
                    render={(record) => getReportCount(record)}
                />
                <DateField source="createdAt" label="Créé le" showTime />
                <DateField source="updatedAt" label="Mis à jour" showTime />
                <FunctionField label="Commentaire" render={renderCommentCard} />
                <FunctionField label="Signalements reçus" render={renderReporterTimeline} />
                <FunctionField label="Actions de modération" render={renderActions} />
            </SimpleShowLayout>
        </Show>
    );
}
